import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import {
  apiFetch,
  refreshAccessToken,
  setSessionTokens,
} from "../../shared/api/client";
import { recordDuration } from "../../shared/utilities/performance";

type AuthState = "booting" | "authenticated" | "anonymous";

interface LoginInput {
  username: string;
  password: string;
}

interface AuthContextValue {
  state: AuthState;
  role: string | null;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>("booting");
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    const started = performance.now();
    refreshAccessToken()
      .then((session) => {
        setRole(session?.role ?? null);
        setState(session ? "authenticated" : "anonymous");
      })
      .finally(() => recordDuration("erp.bootstrap", started));
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const response = await apiFetch<{
      access_token: string;
      csrf_token: string;
      role: string;
    }>("/api/v2/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    });
    setSessionTokens({
      accessToken: response.access_token,
      csrfToken: response.csrf_token,
    });
    setRole(response.role);
    setState("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/v2/auth/logout", { method: "POST" });
    } finally {
      setSessionTokens({ accessToken: null, csrfToken: null });
      setRole(null);
      setState("anonymous");
    }
  }, []);

  const value = useMemo(
    () => ({ state, role, login, logout }),
    [state, role, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
